import {
  AlertTriangle,
  Bluetooth,
  CheckCircle2,
  ExternalLink,
  Laptop,
  Printer,
  RefreshCw,
  Server,
  ShieldCheck,
  Smartphone,
  Wifi
} from "lucide-react";
import { useEffect, useState } from "react";
import { useAuth } from "../AuthContext";
import { api } from "../api";
import { isHostedBaseUrl, networkReadinessHeading } from "../networkReadiness";
import type { PrinterStatus, SystemInformation } from "../types";

export function SettingsPage() {
  const { user, deviceLabel, setDeviceLabel } = useAuth();
  const [system, setSystem] = useState<SystemInformation | null>(null);
  const [printer, setPrinter] = useState<PrinterStatus | null>(null);
  const [baseUrl, setBaseUrl] = useState("");
  const [printerTarget, setPrinterTarget] = useState("");
  const [confirmGeneric, setConfirmGeneric] = useState(false);
  const [printerWorking, setPrinterWorking] = useState(false);
  const [message, setMessage] = useState("");

  async function load() {
    try {
      const [systemValue, printerValue] = await Promise.all([
        api.system(),
        api.printerStatus()
      ]);
      setSystem(systemValue);
      setBaseUrl(String(systemValue.base_url || ""));
      setPrinter(printerValue);
      setPrinterTarget(printerValue.configured_target || "");
      setConfirmGeneric(false);
    } catch (reason) {
      setMessage(reason instanceof Error ? reason.message : "Could not load system settings");
    }
  }

  useEffect(() => {
    void load();
  }, []);
  const storage = system?.storage;
  const network = system?.network;
  const hosted = Boolean(system && isHostedBaseUrl(system.base_url));
  const selectedPrinter = printer?.targets.find(
    (target) => target.identifier === printerTarget
  );
  return (
    <div className="page narrow-page">
      <div className="page-heading">
        <div>
          <h1>System & device</h1>
          <p>
            Server access, connectivity, storage health, and this browser's voluntary label.
          </p>
        </div>
      </div>
      {system && hosted && (
        <section className="travel-readiness ready">
          <div className="travel-readiness-head">
            <span className="travel-readiness-icon">
              <ShieldCheck />
            </span>
            <div>
              <span className="eyebrow">Hosted access</span>
              <h2>Secure iPhone access is ready</h2>
              <p>Encrypted public address · private inventory login required</p>
            </div>
            <span className="readiness-pill ready">Online</span>
          </div>
          <div className="network-url-grid">
            <div>
              <Server />
              <span>Secure web address</span>
              <a href={system.base_url} target="_blank" rel="noreferrer">
                {system.base_url}
                <ExternalLink />
              </a>
            </div>
            <div>
              <Smartphone />
              <span>iPhones and NFC tags use</span>
              <strong>{system.base_url}</strong>
            </div>
            <div>
              <Wifi />
              <span>Wi-Fi and travel changes</span>
              <strong>No local IP update required</strong>
            </div>
          </div>
          {system.tag_url_migration.tags_on_other_base_urls > 0 && (
            <p className="tag-migration-note">
              <strong>{system.tag_url_migration.tags_on_other_base_urls} tag(s)</strong> were
              generated with an older base address. Their inventory history is safe, but those
              physical tags should be rewritten to {system.base_url}.
            </p>
          )}
          <p className="travel-rule">
            This hosted address works across Wi-Fi and cellular networks. Inventory remains
            private: users must sign in before a scanned NFC tag reveals its record.
          </p>
        </section>
      )}
      {network && !hosted && (
        <section className={`travel-readiness ${network.phone_access_ready ? "ready" : "attention"}`}>
          <div className="travel-readiness-head">
            <span className="travel-readiness-icon">
              {network.phone_access_ready ? <ShieldCheck /> : <AlertTriangle />}
            </span>
            <div>
              <span className="eyebrow">Travel network check</span>
              <h2>{networkReadinessHeading(network)}</h2>
              <p>
                {network.network_name || "Current network"} ·{" "}
                {network.network_category || "profile not detected"} ·{" "}
                {network.recommended_interface || "network adapter"}
              </p>
            </div>
            <span className={`readiness-pill ${network.phone_access_ready ? "ready" : ""}`}>
              {network.phone_access_ready ? "Ready" : "Check network"}
            </span>
          </div>
          <div className="network-url-grid">
            <div>
              <Laptop />
              <span>Desktop always opens here</span>
              <strong>{network.desktop_url}</strong>
            </div>
            <div>
              <Smartphone />
              <span>iPhones use this network address</span>
              <a href={network.current_lan_url} target="_blank" rel="noreferrer">
                {network.current_lan_url}
                <ExternalLink />
              </a>
            </div>
            <div>
              <Wifi />
              <span>Address written to new NFC tags</span>
              <strong>{system.base_url}</strong>
            </div>
          </div>
          {network.warnings.length > 0 && (
            <div className="network-warning-list">
              {network.warnings.map((warning) => (
                <p key={warning}>
                  <AlertTriangle />
                  {warning}
                </p>
              ))}
            </div>
          )}
          {system.tag_url_migration.tags_on_other_base_urls > 0 && (
            <p className="tag-migration-note">
              <strong>{system.tag_url_migration.tags_on_other_base_urls} tag(s)</strong> were
              generated with an older base address. Their inventory history is safe, but those
              physical tags may need to be rewritten before the old network is unavailable.
            </p>
          )}
          <p className="travel-rule">
            On a new trusted Wi-Fi, open the desktop shortcut first. It refreshes this check.
            Windows may ask you to mark a new network as Private; never do that on public hotel
            or airport Wi-Fi.
          </p>
        </section>
      )}
      <div className="settings-list">
        <section>
          <Server />
          <div>
            <span>{hosted ? "Hosted server" : "Windows computer"}</span>
            <strong>{String(system?.hostname || "Loading…")}</strong>
          </div>
        </section>
        {user?.role === "administrator" && !hosted && (
          <section className="printer-settings">
            <Printer />
            <label>
              D450 wireless thermal printer
              <select
                value={printerTarget}
                onChange={(event) => {
                  setPrinterTarget(event.target.value);
                  setConfirmGeneric(false);
                }}
              >
                <option value="">Not configured</option>
                {printer?.targets.map((target) => (
                  <option value={target.identifier} key={target.identifier}>
                    {target.display_name}
                    {target.d450 ? " · recommended" : " · verify this is the D450"}
                  </option>
                ))}
              </select>
              <small>
                  Standard roll: 100 × 150 mm portrait (long side vertical), 3 mm gap,
                  203 DPI. Only discovered Bluetooth serial ports are selectable.
              </small>
              {!printer?.targets.length && (
                <span className="printer-help">
                  <Bluetooth />
                  No D450 Bluetooth serial target is visible. Pair the printer in Windows
                  Bluetooth settings, then refresh.
                </span>
              )}
              {selectedPrinter && !selectedPrinter.d450 && (
                <span className="confirm-row">
                  <input
                    type="checkbox"
                    checked={confirmGeneric}
                    onChange={(event) => setConfirmGeneric(event.target.checked)}
                  />
                  I confirm this generic Bluetooth port belongs to the D450.
                </span>
              )}
              <span className={printer?.ready ? "printer-state ready" : "printer-state"}>
                {printer?.ready ? <CheckCircle2 /> : <RefreshCw />}
                {printer?.message || "Checking the Windows printer connection…"}
              </span>
              <div className="settings-actions">
                <button
                  className="button primary"
                  disabled={
                    printerWorking ||
                    Boolean(selectedPrinter && !selectedPrinter.d450 && !confirmGeneric)
                  }
                  onClick={async () => {
                    setPrinterWorking(true);
                    try {
                      await api.updatePrinterConfiguration(
                        printerTarget || null,
                        confirmGeneric
                      );
                      setMessage(
                        printerTarget
                          ? "D450 printer configuration saved."
                          : "Thermal printer configuration cleared."
                      );
                      await load();
                    } catch (reason) {
                      setMessage(
                        reason instanceof Error
                          ? reason.message
                          : "Could not save the printer configuration"
                      );
                    } finally {
                      setPrinterWorking(false);
                    }
                  }}
                >
                  Save printer
                </button>
                <button className="button secondary" onClick={() => void load()}>
                  <RefreshCw />
                  Refresh ports
                </button>
                <button
                  className="button secondary"
                  disabled={printerWorking || !printer?.ready}
                  onClick={async () => {
                    setPrinterWorking(true);
                    try {
                      const result = await api.printTestLabel();
                      setMessage(result.message);
                      await load();
                    } catch (reason) {
                      setMessage(
                        reason instanceof Error ? reason.message : "Test label failed"
                      );
                    } finally {
                      setPrinterWorking(false);
                    }
                  }}
                >
                  <Printer />
                  Send test label
                </button>
              </div>
              {printer?.last_job && (
                <small>
                  Last job: {printer.last_job.event_type === "label_printed" ? "sent" : "failed"}
                  {printer.last_job.stock_number
                    ? ` · ${printer.last_job.stock_number}`
                    : ""}
                  {" · "}
                  {new Intl.DateTimeFormat(undefined, {
                    dateStyle: "medium",
                    timeStyle: "short"
                  }).format(new Date(printer.last_job.timestamp))}
                </small>
              )}
            </label>
          </section>
        )}
        {user?.role === "administrator" && hosted && (
          <section>
            <Printer />
            <div>
              <span>Thermal label printing</span>
              <strong>{printer?.message || "Checking the Windows print bridge…"}</strong>
              <small>
                Hosted print jobs are securely relayed to the D450 Bluetooth port on this laptop.
                The exact-size PDF remains available as a manual fallback.
              </small>
            </div>
          </section>
        )}
        {user?.role === "administrator" && (
          <section>
            <Wifi />
            <label>
              NFC tag base URL
              <input
                value={baseUrl}
                onChange={(event) => setBaseUrl(event.target.value)}
                placeholder="http://family-move-inventory.local:8080"
              />
              <small>Test this exact address on every iPhone before provisioning tags.</small>
              <button
                className="button secondary"
                onClick={async () => {
                  try {
                    const result = await api.updateBaseUrl(baseUrl);
                    setMessage(
                      `Saved ${result.base_url}. Restart is recommended; existing physical tags may need rewriting.`
                    );
                  } catch (reason) {
                    setMessage(reason instanceof Error ? reason.message : "Could not save base URL");
                  }
                }}
              >
                Save base URL
              </button>
            </label>
          </section>
        )}
        <section>
          <Wifi />
          <div>
            <span>{hosted ? "Hosted address" : "Current IPv4 address"}</span>
            <strong>
              {hosted
                ? system?.base_url || "Loading…"
                : `${system?.current_ipv4 || "Loading…"}:${system?.port || ""}`}
            </strong>
            <small>
              {hosted
                ? "Stable across home, travel, Wi-Fi, and cellular networks"
                : `Configured tag base: ${system?.base_url || "Loading…"}`}
            </small>
          </div>
        </section>
        <section>
          <Smartphone />
          <label>
            This device label
            <input
              value={deviceLabel}
              onChange={(event) => setDeviceLabel(event.target.value)}
              placeholder="Family iPhone"
            />
            <small>No fingerprinting or hardware identifier is collected.</small>
          </label>
        </section>
      </div>
      {system && (
        <dl className="system-facts">
          <div>
            <dt>Hostname resolution</dt>
            <dd>{system.hostname_reachable ? "Reachable" : "Not confirmed on this network"}</dd>
          </div>
          <div>
            <dt>Database integrity</dt>
            <dd>{system.database_integrity}</dd>
          </div>
          <div>
            <dt>Photo storage</dt>
            <dd>{storage?.photo_folder_available ? "Available" : "Unavailable"}</dd>
          </div>
          <div>
            <dt>Trusted household mode</dt>
            <dd>{system.trusted_household_mode ? "Enabled" : "Disabled"}</dd>
          </div>
        </dl>
      )}
      {message && <div className="alert info">{message}</div>}
    </div>
  );
}
